// Accès à l'API de la console. Une seule fonction, parce que l'API est petite
// et homogène : elle renvoie du JSON et signale ses erreurs par un champ
// `error` avec un statut HTTP parlant.

import type { FaitsOffre } from '../../faitsOffre'
import type { CompatibilityResult } from '@ai-job-fit/engine/scoring'
import { visite } from './mesure'

export class ApiError extends Error {
  readonly status: number
  /** Présent quand l'UI doit réagir au refus, pas seulement l'afficher. */
  readonly code?: string
  constructor(status: number, message: string, code?: string) {
    super(message)
    this.status = status
    this.code = code
  }
}

export async function api<T = any>(path: string, opts: RequestInit = {}): Promise<T> {
  const isForm = opts.body instanceof FormData
  const res = await fetch(path, {
    ...opts,
    // La visite (issue #193) relie les pas d'une inscription au compte qu'elle
    // crée : le serveur la joint aux events qu'il envoie à PostHog.
    headers: isForm
      ? { 'x-radar-visite': visite(), ...opts.headers }
      : { 'content-type': 'application/json', 'x-radar-visite': visite(), ...opts.headers },
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new ApiError(res.status, (data as any).error || `Erreur ${res.status}`, (data as any).code)
  return data as T
}

export const get = <T = any>(path: string) => api<T>(path)

export const post = <T = any>(path: string, body?: unknown) =>
  api<T>(path, {
    method: 'POST',
    body: body instanceof FormData ? body : body === undefined ? undefined : JSON.stringify(body),
  })

/** PUT pour les remplacements COMPLETS : les catégories d'une fiche sont
 *  envoyées en entier, jamais ajoutées une à une. Un PATCH obligerait à
 *  distinguer « je retire cette catégorie » de « je ne l'ai pas envoyée », et
 *  la seconde lecture effacerait tout à la première requête incomplète. */
export const put = <T = any>(path: string, body?: unknown) =>
  api<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) })

// --- Formes renvoyées par l'API -------------------------------------------

export interface Summary {
  offersTotal: number
  offers7d: number
  /** Offres qui attendent une décision — le badge de la page Décisions. */
  pile: number
  /** Le seuil qui a filtré `pile`, pour que la page puisse le dire. */
  pileMinScore: number
  /** Pages suivies au plus par compte Radar (issue #86), `null` en formule Complet (#121). */
  plafondSuivis: number | null
  aRevoir: number
  suiviActifs: number
  places: number
  dossiers: number
  dossiers7d: number
  sourcesInError: number
  sourcesTotal: number
  consultants: number
  lastRun: string | null
}

/** Le statut de TRI d'une offre : ce que le BM a décidé d'en faire. */
export type OfferStatus = 'todo' | 'later' | 'skip'

/** L'avancement commercial d'un consultant proposé sur une offre. */
export type CandidatureStatus =
  | 'a_envoyer' | 'envoye' | 'relance' | 'entretien' | 'place' | 'refuse'

export interface CandidatureRow {
  id: number
  consultantId: number
  name: string
  label: string
  status: CandidatureStatus
  created_at: string
  updated_at: string
  score: number | null
  dossierId: number | null
}

export interface SuiviRow extends CandidatureRow {
  /** Date de l'étape en cours (dernier `candidature.status` du journal, ou la
   *  création) : la relance se compte depuis elle. */
  statusAt: string
  offerId: number
  title: string
  /** L'employeur, ou null s'il est inconnu. */
  company: string | null
  /** Le site d'où vient l'offre : une provenance, jamais l'employeur. */
  source_name: string
  /** Adresse du logo de l'employeur, ou null pour le monogramme. */
  logo: string | null
  offerUrl: string | null
  offerArchived: boolean
  /** Le premier passage à « envoyée », ou null (issue #170). */
  envoyeeLe: string | null
}

export interface OfferRow {
  id: number
  title: string
  location: string | null
  url: string | null
  first_seen_at: string
  /** Date du dernier tri de ce compte (Plus tard, Pas pour moi). */
  status_at?: string | null
  /** L'employeur, ou null s'il est inconnu : jamais le nom du site (cf.
   *  `colonneEmployeur`). La provenance est `source_name`. */
  company: string | null
  /** Adresse du logo de l'employeur, ou null pour le monogramme (cf. `colonneLogo`). */
  logo: string | null
  source_name: string
  platform: string
  /** 'emploi' (défaut) ou 'mission' — porté par la SOURCE, jamais ambigu par
   *  offre : cf. docs/chantier-radar-console.md §6. */
  kind: 'emploi' | 'mission'
  /** Radar : date de la dernière lecture de son site quand personne ne le
   *  suivait alors et qu'il n'a pas été relu depuis (issue #131), sinon null.
   *  L'offre n'est alors pas fraîche : elle est peut-être fermée. */
  sourceARelire?: string | null
  /** Republiée, ou en ligne depuis longtemps (issue #161) : null sinon. */
  signaux?: { republications: number; retireeLe: string | null; ageJours: number } | null
  read: boolean
  status: OfferStatus
  /** `verdict`/`result` : posés seulement en Radar (un profil borné côté
   *  serveur), voir le commentaire de `listOffers` côté serveur. */
  matches: {
    consultantId: number
    name: string
    label: string
    score: number
    verdict?: string
    result?: CompatibilityResult
  }[]
  candidatures: CandidatureRow[]
  // Les cinq champs suivants sont NULL sur une offre d'emploi : seules les
  // sources `kind = 'mission'` (Connecting-Expertise) les remplissent.
  deadline_at: string | null
  starts_at?: string | null
  ends_at?: string | null
  proposals_count: number | null
  framework?: string | null
}

export interface Candidate {
  consultant_id: number
  name: string
  label: string
  score: number
  potential_score: number
  verdict: string
  affinity: number | null
  archived_at: string | null
  result: any
  /** Micro-retour 👍/👎 donné sur ce score, ou `null` si personne n'a répondu. */
  feedback: 'up' | 'down' | null
}

/** Une question d'écart posée avant de générer un dossier. Même forme que
 *  `OfferQuestion` de `@ai-job-fit/engine` : pas d'import du moteur ici, cet
 *  écran n'en a besoin d'aucun autre bout. */
export interface GapQuestion {
  topic: string
  question: string
}

export interface OfferDetail extends Omit<OfferRow, 'matches'> {
  description: string | null
  /** Contrat, télétravail, salaire… lus la nuit ; null tant qu'elle n'est pas lue. */
  faits: FaitsOffre | null
  candidates: Candidate[]
  dossiers: { id: number; consultant_id: number; consultant_name: string; created_at: string }[]
  events: { id: number; type: string; author: string; payload: string | null; created_at: string }[]
}

export interface ConsultantRow {
  id: number
  name: string
  label: string
  status: 'available' | 'placed' | 'archived'
  cv_filename: string | null
  created_at: string
  updated_at: string
  archived: boolean
  location: any
  skills: any[]
  stats: { scored: number; avg_score: number | null; best_score: number | null; go_count: number; positioned: number }
}

export interface SourceRow {
  id: number
  name: string
  company: string | null
  /** Adresse du logo de l'entreprise, ou null pour le monogramme. */
  logo: string | null
  url: string
  platform: string
  status: string
  last_checked_at: string | null
  last_error: string | null
  /** Début de la panne en cours, ou null si la source marche. */
  error_since: string | null
  offers: number
  archived_at: string | null
  /** Offres qui reviendraient si on restaurait la source. */
  archived_offers: number
  /** Est-ce que MOI je suis ce site (lot G). Toujours vrai en console, où il
   *  n'y a pas d'abonnement : le business manager voit toute sa base. */
  abonne: number | boolean
  /** Combien de personnes le suivent. Sert à dire, côté exploitant, quels sites
   *  ne sont plus interrogés faute d'abonné. */
  abonnes: number
  /** Radar : lu alors que personne ne le suivait, pas relu depuis (issue
   *  #131, `colonneARelire`). Ses offres datent de `last_checked_at`. */
  a_relire: number | boolean
  /** Radar : offres ouvertes rangées dans un métier du profil (sans IA).
   *  `null` en console, où il n'y a pas de profil de session. */
  dans_metiers: number | null
  /** Site d'annonces (jobs.lu, ICTJOB) : il publie pour des centaines
   *  d'employeurs, cf. `sourceMultiEmployeurs` côté serveur. */
  annonces: number | boolean
}
